/**
 * Formatacao para o publico brasileiro.
 *
 * Centralizada porque valor fiscal escrito de dois jeitos diferentes na mesma
 * tela e um convite a erro de conferencia.
 */

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const decimal = new Intl.NumberFormat('pt-BR');

export const money = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : brl.format(value);

export const number = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : decimal.format(value);

export const dateTime = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

export const time = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

export const date = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

export const cnpj = (value: string | null | undefined): string => {
  if (!value) return '—';
  const d = value.replace(/\D/g, '');
  if (d.length !== 14) return value;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

/** Documento do consumidor mascarado — o portal nao precisa expor o CPF inteiro. */
export const maskedDocument = (value: string | null | undefined): string => {
  if (!value) return 'Consumidor';
  const d = value.replace(/\D/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}.***.***-${d.slice(9)}`;
  return cnpj(d);
};

export const accessKey = (value: string | null | undefined): string =>
  value ? (value.match(/.{1,4}/g) ?? []).join(' ') : '—';

/** "19h 33min" — como o operador fala do prazo de contingencia. */
export const hoursRemaining = (hours: number | null): string => {
  if (hours === null) return '—';
  if (hours <= 0) return 'prazo vencido';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}min` : `${m}min`;
};

export const MODEL_LABEL: Record<string, string> = {
  nfe: 'NF-e',
  nfce: 'NFC-e',
  nfse: 'NFS-e',
};

export const STATUS_LABEL: Record<string, string> = {
  queued: 'Na fila',
  processing: 'Processando',
  authorized: 'Autorizada',
  rejected: 'Rejeitada',
  denied: 'Denegada',
  contingency: 'Contingência',
  cancelled: 'Cancelada',
  error: 'Erro',
};

export type PillTone = 'ok' | 'warn' | 'bad' | 'info' | 'mute';

export const STATUS_TONE: Record<string, PillTone> = {
  queued: 'info',
  processing: 'info',
  authorized: 'ok',
  rejected: 'bad',
  denied: 'bad',
  contingency: 'warn',
  cancelled: 'mute',
  error: 'bad',
};

/** Regime tributario do emitente (campo CRT do XML), na linguagem do usuario. */
export const CRT_LABEL: Record<string, string> = {
  '1': 'Simples Nacional',
  '2': 'Simples Nacional — excesso de sublimite',
  '3': 'Regime Normal',
  '4': 'MEI',
};

export const ROLE_LABEL: Record<string, string> = {
  owner: 'Proprietário',
  admin: 'Administrador',
  financeiro: 'Financeiro',
  operador: 'Operador',
  leitura: 'Somente leitura',
};
